import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft, X, Plus, ZoomIn, ZoomOut, ArrowLeft, ArrowRight, Copy, Clipboard, Trash2, Upload, Download } from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { databases, DATABASE_ID, COLLECTIONS, Query, account } from '../../lib/appwrite';
import { parseExcelFile, validateImportedSamples, downloadImportTemplate } from '../../utils/excelImport';
import { sampleService } from '../../services/sampleServiceAppwrite';

const BoxViewSampleManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get initial data from navigation state
  const initialBoxData = location?.state?.boxData || null;
  const [currentDewarId] = useState(initialBoxData?.dewarId || null);
  const [currentRackId] = useState(initialBoxData?.rackId || null);
  const [currentBoxId, setCurrentBoxId] = useState(initialBoxData?.boxId || null);

  // State for box and samples data
  const [currentBox, setCurrentBox] = useState(null);
  const [currentDewar, setCurrentDewar] = useState(null);
  const [samples, setSamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Tabs management for multiple boxes
  const [tabs, setTabs] = useState(() => {
    if (initialBoxData) {
      return [{
        id: initialBoxData?.boxId,
        dewarId: initialBoxData?.dewarId,
        rackId: initialBoxData?.rackId,
        boxId: initialBoxData?.boxId,
        boxName: initialBoxData?.boxName,
        dewarName: initialBoxData?.dewarName,
        rackNumber: initialBoxData?.rackNumber
      }];
    }
    return [];
  });
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  // Grid view state
  const [zoomLevel, setZoomLevel] = useState(1);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [selectedCapColor, setSelectedCapColor] = useState('red');
  const [showSampleForm, setShowSampleForm] = useState(false);
  const [hoveredPosition, setHoveredPosition] = useState(null);

  // Sample form data
  const [sampleFormData, setSampleFormData] = useState({
    sampleName: '',
    freezeDate: '',
    frozenBy: '',
    pi: '',
    passageNumber: '',
    notes: ''
  });

  // Copy/Paste functionality state
  const [copiedSample, setCopiedSample] = useState(null);
  const [showCopyNotification, setShowCopyNotification] = useState(false);

  // NEW: Multi-selection state for click-based selection
  const [selectedCells, setSelectedCells] = useState(new Set());
  const [isCopyMode, setIsCopyMode] = useState(false);

  // NEW: Multi-selection state for delete functionality - MODIFIED for toggle behavior
  const [selectedSamples, setSelectedSamples] = useState(new Set());
  const [isDeleteMode, setIsDeleteMode] = useState(false);

  // Add this block - Missing drag state variables
  const [isDragging, setIsDragging] = useState(false);
  const [draggedPositions, setDraggedPositions] = useState(new Set());

  // NEW: Import state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importResults, setImportResults] = useState(null);

  // NEW: Fetch box and samples data from Appwrite
  useEffect(() => {
    if (currentBoxId) {
      loadBoxData();
    }
  }, [currentBoxId]);

  // Load box and samples data from Appwrite
  const loadBoxData = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch box details
      const boxData = await databases.getDocument(
        DATABASE_ID,
        COLLECTIONS.BOXES,
        currentBoxId
      );

      // Fetch samples for this box
      const samplesResponse = await databases.listDocuments(
        DATABASE_ID,
        COLLECTIONS.SAMPLES,
        [Query.equal('box_id', currentBoxId)]
      );

      // Fetch dewar details for liquid nitrogen level display
      if (currentDewarId) {
        try {
          const dewarData = await databases.getDocument(
            DATABASE_ID,
            COLLECTIONS.DEWARS,
            currentDewarId
          );

          setCurrentDewar({
            id: dewarData.$id,
            name: dewarData.name,
            liquidNitrogenLevel: dewarData.liquid_nitrogen_level || 14
          });
        } catch (dewarError) {
          console.warn('Could not fetch dewar details:', dewarError);
        }
      }

      setCurrentBox({
        id: boxData.$id,
        name: boxData.name,
        type: boxData.box_type,
        samples: boxData.max_capacity,
        occupied: boxData.occupied_count,
        rackId: boxData.rack_id
      });

      setSamples(samplesResponse.documents || []);
    } catch (err) {
      console.error('Error loading box data:', err);
      setError(err?.message || 'Failed to load box data');
    } finally {
      setLoading(false);
    }
  };

  // NEW: Generate grid with current samples
  const generateGrid = () => {
    if (!currentBox) return [];
    
    const gridSize = currentBox?.type === '9x9' ? 9 : 10;
    const alphabet = 'ABCDEFGHIJ';
    const grid = [];

    for (let row = 0; row < gridSize; row++) {
      for (let col = 0; col < gridSize; col++) {
        const position = `${alphabet?.[row]}${col + 1}`;
        
        // Find sample at this position
        const sample = samples?.find(s => 
          s?.position === position && 
          s?.row_index === row && 
          s?.col_index === col
        );

        grid?.push({
          position,
          row,
          col,
          occupied: !!sample,
          capColor: sample?.cap_color || null,
          sampleId: sample?.sample_id || null,
          sampleName: sample?.name || null,
          freezeDate: sample?.freeze_date || null,
          frozenBy: sample?.frozen_by || null,
          pi: sample?.pi || null,
          passageNumber: sample?.passage_number || null,
          notes: sample?.notes || null,
          dbId: sample?.$id || null,
          originalSampleId: sample?.original_sample_id || null
        });
      }
    }

    return grid;
  };

  const sampleGrid = generateGrid();

  // Get current breadcrumb data
  const getBreadcrumbData = () => {
    const activeTab = tabs?.[activeTabIndex];
    if (!activeTab) return { dewarName: '', rackNumber: '', boxName: '' };
    
    return {
      dewarName: activeTab?.dewarName,
      rackNumber: activeTab?.rackNumber,
      boxName: activeTab?.boxName
    };
  };

  const breadcrumb = getBreadcrumbData();

  // Handle tab switching
  const switchTab = (index) => {
    const tab = tabs?.[index];
    if (tab) {
      setActiveTabIndex(index);
      setCurrentBoxId(tab?.boxId);
      setSelectedPosition(null);
      setShowSampleForm(false);
    }
  };

  // Add new tab
  const addNewTab = () => {
    if (tabs?.length >= 5) {
      alert('Maximum 5 tabs allowed');
      return;
    }

    navigate('/dewars');
  };

  // Close tab
  const closeTab = (index, e) => {
    e?.stopPropagation();
    
    if (tabs?.length === 1) {
      navigate('/dewars');
      return;
    }

    const newTabs = tabs?.filter((_, i) => i !== index);
    setTabs(newTabs);

    if (activeTabIndex >= newTabs?.length) {
      setActiveTabIndex(newTabs?.length - 1);
    } else if (activeTabIndex === index && index > 0) {
      setActiveTabIndex(index - 1);
    }

    const newActiveTab = newTabs?.[activeTabIndex >= newTabs?.length ? newTabs?.length - 1 : activeTabIndex];
    if (newActiveTab) {
      setCurrentBoxId(newActiveTab?.boxId);
    }
  };

  // Navigate between boxes in same rack
  const navigateToAdjacentBox = (direction) => {
    // Note: dewars data is not available in this component
    // This function requires data from parent component or database
    // For now, we'll disable this functionality
    console.warn('Navigate to adjacent box: dewars data not available');
    return;
  };

  // Handle sample copy
  const handleCopySample = (position, e) => {
    if (e) {
      e?.preventDefault();
      e?.stopPropagation();
    }

    const cell = sampleGrid?.find(c => c?.position === position);
    
    if (!cell?.occupied) {
      return;
    }

    const copiedData = {
      sampleName: cell?.sampleName,
      freezeDate: cell?.freezeDate,
      frozenBy: cell?.frozenBy,
      pi: cell?.pi,
      passageNumber: cell?.passageNumber,
      notes: cell?.notes,
      capColor: cell?.capColor,
      originalSampleId: cell?.originalSampleId || cell?.sampleId
    };

    setCopiedSample(copiedData);
    setShowCopyNotification(true);

    setTimeout(() => {
      setShowCopyNotification(false);
    }, 2000);
  };

  // Handle sample paste
  const handlePasteSample = (position) => {
    if (!copiedSample) return;

    const cell = sampleGrid?.find(c => c?.position === position);
    
    if (cell?.occupied) {
      if (!window?.confirm('This position is already occupied. Do you want to overwrite it?')) {
        return;
      }
    }

    setSampleFormData({
      sampleName: copiedSample?.sampleName || '',
      freezeDate: copiedSample?.freezeDate || '',
      frozenBy: copiedSample?.frozenBy || '',
      pi: copiedSample?.pi || '',
      passageNumber: copiedSample?.passageNumber || '',
      notes: copiedSample?.notes || '',
      originalSampleId: copiedSample?.originalSampleId
    });
    setSelectedCapColor(copiedSample?.capColor || 'red');
    setSelectedPosition(position);
    setShowSampleForm(true);
  };

  // NEW: Handle keyboard shortcuts - MODIFIED to remove Ctrl+X shortcut
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Escape key to clear selection and exit modes
      if (e?.key === 'Escape') {
        setSelectedSamples(new Set());
        setIsDeleteMode(false);
        setSelectedCells(new Set());
        setIsCopyMode(false);
        setCopiedSample(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedSamples]);

  // MODIFIED: Handle right-click to enter copy mode - ONLY for occupied cells
  const handleCellRightClick = (position, e) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    const cell = sampleGrid?.find(c => c?.position === position);
    
    // Only allow copying from occupied cells
    if (!cell?.occupied || !cell?.sampleId) {
      return;
    }

    // Clear any existing deletion selections
    setSelectedSamples(new Set());
    setIsDeleteMode(false);

    // Copy sample data
    const copiedData = {
      sampleName: cell?.sampleName,
      freezeDate: cell?.freezeDate,
      frozenBy: cell?.frozenBy,
      pi: cell?.pi,
      passageNumber: cell?.passageNumber,
      notes: cell?.notes,
      capColor: cell?.capColor,
      originalSampleId: cell?.originalSampleId || cell?.sampleId
    };

    setCopiedSample(copiedData);
    setShowCopyNotification(true);

    // Enter copy mode - clear previous selections and start fresh
    setIsCopyMode(true);
    setSelectedCells(new Set()); // Start with empty selection

    setTimeout(() => {
      setShowCopyNotification(false);
    }, 2000);
  };

  // MODIFIED: Handle cell click - prioritize copy mode, remove deletion toggle
  const handleCellClick = (position, e) => {
    const cell = sampleGrid?.find(c => c?.position === position);
    
    // Ensure cell object exists before proceeding
    if (!cell) {
      console.error('Cell not found for position:', position);
      return;
    }
    
    // PRIORITY 1: If in copy mode, handle cell selection for copying
    if (isCopyMode) {
      e?.preventDefault();
      e?.stopPropagation();
      
      // Only allow selecting EMPTY cells in copy mode
      if (!cell?.occupied && !cell?.dbId) {
        setSelectedCells(prev => {
          const newSet = new Set(prev);
          if (newSet?.has(position)) {
            newSet?.delete(position);
          } else {
            newSet?.add(position);
          }
          return newSet;
        });
      } else {
        // Warn user that occupied cells cannot be selected
        console.warn('Cannot select occupied cell in copy mode:', position);
      }
      return;
    }

    // PRIORITY 2: Clear deletion selections when clicking anywhere
    if (selectedSamples?.size > 0) {
      setSelectedSamples(new Set());
      setIsDeleteMode(false);
    }

    // PRIORITY 3: Normal empty cell click - show form to add sample
    if (!cell?.occupied) {
      setSelectedPosition(position);
      setSampleFormData({
        sampleName: '',
        freezeDate: '',
        frozenBy: '',
        pi: '',
        passageNumber: '',
        notes: ''
      });
      setShowSampleForm(true);
    }
  };

  // IMPROVED: Complete copy with enhanced empty cell validation
  const handleCompleteCopy = async () => {
    if (!isCopyMode || selectedCells?.size === 0) {
      alert('Please select at least one cell to copy samples to.');
      return;
    }

    if (!copiedSample) {
      alert('No sample data to copy. Please right-click an occupied cell first.');
      return;
    }

    // CRITICAL: Validate that all required fields exist in copiedSample
    if (!copiedSample?.sampleName || !copiedSample?.freezeDate || !copiedSample?.frozenBy || !copiedSample?.pi) {
      alert('Cannot copy sample: Missing required fields (name, freeze date, frozen by, or PI). Please copy a complete sample.');
      return;
    }

    // Filter for truly empty positions only
    const selectedEmptyPositions = Array.from(selectedCells)?.filter(pos => {
      const cell = sampleGrid?.find(c => c?.position === pos);
      return cell && !cell?.occupied && !cell?.dbId;
    });
    
    if (selectedEmptyPositions?.length === 0) {
      alert('No empty cells selected. Please select cells that do not contain samples.');
      return;
    }

    try {
      setError('');
      const user = await account.get();
      if (!user) throw new Error('Not authenticated');

      const alphabet = 'ABCDEFGHIJ';
      // Convert to string for Appwrite (passage_number is string type in collection)
      const passageNumberStr = copiedSample?.passageNumber ? String(copiedSample?.passageNumber) : '';

      // Convert freeze_date to ISO format for Appwrite datetime type
      const freezeDateISO = copiedSample?.freezeDate ? new Date(copiedSample.freezeDate).toISOString() : new Date().toISOString();

      // Insert samples into all selected empty positions
      const insertPromises = selectedEmptyPositions?.map(position => {
        const rowIndex = alphabet?.indexOf(position?.[0]);
        const colIndex = parseInt(position?.slice(1)) - 1;
        const sampleId = `S${Date.now()?.toString()?.slice(-6)}${Math.random()?.toString(36)?.slice(2, 5)}`;

        return databases.createDocument(
          DATABASE_ID,
          COLLECTIONS.SAMPLES,
          sampleId + '_' + position, // Use combination to ensure unique ID
          {
            box_id: currentBoxId,
            user_id: user.$id,
            sample_id: sampleId,
            name: copiedSample?.sampleName,
            position: position,
            row_index: rowIndex,
            col_index: colIndex,
            cap_color: copiedSample?.capColor,
            freeze_date: freezeDateISO,
            frozen_by: copiedSample?.frozenBy,
            pi: copiedSample?.pi,
            passage_number: passageNumberStr,
            notes: copiedSample?.notes || null,
            original_sample_id: copiedSample?.originalSampleId || null
          }
        );
      });

      await Promise.all(insertPromises);

      // Reload box data
      await loadBoxData();

      // Exit copy mode and clear selections
      setIsCopyMode(false);
      setSelectedCells(new Set());
      setCopiedSample(null);

      // Show success message
      alert(`Successfully copied sample to ${selectedEmptyPositions?.length} position${selectedEmptyPositions?.length > 1 ? 's' : ''}`);
    } catch (err) {
      console.error('Error copying samples:', err);
      setError(err?.message || 'Failed to copy samples');
      alert(`Error: ${err?.message || 'Failed to copy samples'}`);
    }
  };

  // MODIFIED: Handle double-click for deletion flag toggle only
  const handleCellDoubleClick = (position, e) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    const cell = sampleGrid?.find(c => c?.position === position);
    
    if (!cell) {
      console.error('Cell not found for position:', position);
      return;
    }
    
    // If cell is occupied, toggle deletion flag
    if (cell?.occupied && cell?.dbId) {
      toggleSampleForDeletion(cell?.dbId);
      return;
    }
    
    // For empty cells, show form to add sample
    if (!cell?.occupied) {
      setSelectedPosition(position);
      setShowSampleForm(true);
    }
  };

  // NEW: Toggle sample for deletion - simplified toggle function
  const toggleSampleForDeletion = (dbId) => {
    if (!dbId) return;

    setSelectedSamples(prev => {
      const newSet = new Set(prev);
      if (newSet?.has(dbId)) {
        newSet?.delete(dbId);
      } else {
        newSet?.add(dbId);
      }
      
      // Enable delete mode when samples are selected
      setIsDeleteMode(newSet?.size > 0);
      
      return newSet;
    });
  };

  // NEW: Batch delete selected samples
  const handleBatchDeleteSamples = async () => {
    if (selectedSamples?.size === 0) return;

    const confirmMessage = `Are you sure you want to delete ${selectedSamples?.size} sample${selectedSamples?.size > 1 ? 's' : ''}?`;
    if (!window.confirm(confirmMessage)) return;

    try {
      setError('');
      
      // Delete all selected samples
      const deletePromises = Array.from(selectedSamples)?.map(sampleId =>
        databases.deleteDocument(
          DATABASE_ID,
          COLLECTIONS.SAMPLES,
          sampleId
        )
      );

      await Promise.all(deletePromises);

      // Reload box data
      await loadBoxData();

      // Clear selection
      setSelectedSamples(new Set());
      setIsDeleteMode(false);
      setShowSampleForm(false);
      setSelectedPosition(null);

      // Show success message
      alert(`Successfully deleted ${selectedSamples?.size} sample${selectedSamples?.size > 1 ? 's' : ''}`);
    } catch (err) {
      console.error('Error deleting samples:', err);
      setError(err?.message || 'Failed to delete samples');
    }
  };

  // MODIFIED: Handle grid cell click - unified with handleCellClick for consistency
  const handleGridCellClick = (position, e) => {
    // Ignore if dragging
    if (isDragging) return;
    
    const cell = sampleGrid?.find(c => c?.position === position);
    
    // Ensure cell exists
    if (!cell) {
      console.error('Cell not found for position:', position);
      return;
    }
    
    // If in copy mode, use the same logic as handleCellClick - ONLY ALLOW EMPTY CELLS
    if (isCopyMode) {
      e?.preventDefault();
      e?.stopPropagation();
      
      // Only allow selecting EMPTY cells in copy mode
      if (!cell?.occupied && !cell?.dbId) {
        setSelectedCells(prev => {
          const newSet = new Set(prev);
          if (newSet?.has(position)) {
            newSet?.delete(position);
          } else {
            newSet?.add(position);
          }
          return newSet;
        });
      } else {
        // Warn user that occupied cells cannot be selected
        console.warn('Cannot select occupied cell in copy mode:', position);
      }
      return;
    }

    if (e?.button === 2 && cell?.occupied) {
      handleCopySample(position, e);
      return;
    }

    // Clear deletion selection when clicking on any cell
    if (selectedSamples?.size > 0) {
      setSelectedSamples(new Set());
      setIsDeleteMode(false);
    }
    
    // Clear drag selection
    setDraggedPositions(new Set());

    // NEW: Single-click on occupied cell to edit sample
    if (cell?.occupied && cell?.dbId) {
      setSelectedPosition(position);
      setSampleFormData({
        sampleName: cell?.sampleName || '',
        freezeDate: cell?.freezeDate || '',
        frozenBy: cell?.frozenBy || '',
        pi: cell?.pi || '',
        passageNumber: cell?.passageNumber || '',
        notes: cell?.notes || ''
      });
      setSelectedCapColor(cell?.capColor || 'red');
      setShowSampleForm(true);
      return;
    }

    if (copiedSample && !cell?.occupied) {
      handlePasteSample(position);
      return;
    }

    // Show form for empty cells
    if (!cell?.occupied) {
      setSelectedPosition(position);
      setSampleFormData({
        sampleName: '',
        freezeDate: '',
        frozenBy: '',
        pi: '',
        passageNumber: '',
        notes: ''
      });
      setShowSampleForm(true);
    }
  };

  // Clear copied sample
  const handleClearCopiedSample = () => {
    setCopiedSample(null);
  };

  // MODIFIED: Handle sample form submit with batch add support
  const handleSampleSubmit = async (e) => {
    e?.preventDefault();

    if (!selectedPosition) return;

    try {
      setError('');
      const user = await account.get();
      if (!user) throw new Error('Not authenticated');

      const alphabet = 'ABCDEFGHIJ';

      // Convert to string for Appwrite (passage_number is string type in collection)
      const passageNumberStr = sampleFormData?.passageNumber ? String(sampleFormData?.passageNumber) : '';

      // Convert freeze_date to ISO format for Appwrite datetime type
      const freezeDateISO = sampleFormData?.freezeDate ? new Date(sampleFormData.freezeDate).toISOString() : new Date().toISOString();

      // Check if this is a batch add operation
      const batchPositions = window.batchAddPositions;
      const positionsToAdd = batchPositions && batchPositions?.length > 0 ? batchPositions : [selectedPosition];

      // Process each position
      for (const position of positionsToAdd) {
        const cell = sampleGrid?.find(c => c?.position === position);
        const rowIndex = alphabet?.indexOf(position?.[0]);
        const colIndex = parseInt(position?.slice(1)) - 1;

        if (cell?.occupied && cell?.dbId) {
          // Update existing sample
          await databases.updateDocument(
            DATABASE_ID,
            COLLECTIONS.SAMPLES,
            cell?.dbId,
            {
              name: sampleFormData?.sampleName,
              cap_color: selectedCapColor,
              freeze_date: freezeDateISO,
              frozen_by: sampleFormData?.frozenBy,
              pi: sampleFormData?.pi,
              passage_number: passageNumberStr,
              notes: sampleFormData?.notes || null
            }
          );
        } else {
          // Insert new sample
          const sampleId = `S${Date.now()?.toString()?.slice(-6)}${Math.random()?.toString(36)?.slice(2, 5)}`;

          await databases.createDocument(
            DATABASE_ID,
            COLLECTIONS.SAMPLES,
            sampleId + '_' + position, // Use combination to ensure unique ID
            {
              box_id: currentBoxId,
              user_id: user.$id,
              sample_id: sampleId,
              name: sampleFormData?.sampleName,
              position: position,
              row_index: rowIndex,
              col_index: colIndex,
              cap_color: selectedCapColor,
              freeze_date: freezeDateISO,
              frozen_by: sampleFormData?.frozenBy,
              pi: sampleFormData?.pi,
              passage_number: passageNumberStr,
              notes: sampleFormData?.notes || null,
              original_sample_id: sampleFormData?.originalSampleId || null
            }
          );
        }
      }

      // Clear batch positions
      window.batchAddPositions = null;

      // Reload box data
      await loadBoxData();

      setShowSampleForm(false);
      setSelectedPosition(null);
      setDraggedPositions(new Set());
      setSelectedSamples(new Set());
      setIsDeleteMode(false);
      setSampleFormData({
        sampleName: '',
        freezeDate: '',
        frozenBy: '',
        pi: '',
        passageNumber: '',
        notes: ''
      });

      // Show success message for batch operations
      if (positionsToAdd?.length > 1) {
        alert(`Successfully added ${positionsToAdd?.length} samples to selected positions`);
      }
    } catch (err) {
      console.error('Error saving sample:', err);
      setError(err?.message || 'Failed to save sample');
    }
  };

  // NEW: Handle sample removal with Appwrite
  const handleRemoveSample = async () => {
    if (!selectedPosition) return;

    if (!window?.confirm('Are you sure you want to remove this sample?')) return;

    try {
      setError('');
      const cell = sampleGrid?.find(c => c?.position === selectedPosition);

      if (!cell?.dbId) {
        throw new Error('Sample ID not found');
      }

      await databases.deleteDocument(
        DATABASE_ID,
        COLLECTIONS.SAMPLES,
        cell?.dbId
      );

      // Reload box data
      await loadBoxData();

      setShowSampleForm(false);
      setSelectedPosition(null);
    } catch (err) {
      console.error('Error removing sample:', err);
      setError(err?.message || 'Failed to remove sample');
    }
  };

  // Color helper
  const getColorClass = (color) => {
    const colorMap = {
      red: 'bg-red-500',
      blue: 'bg-blue-500',
      green: 'bg-green-500',
      yellow: 'bg-yellow-500',
      purple: 'bg-purple-500',
      orange: 'bg-orange-500',
      pink: 'bg-pink-500',
      teal: 'bg-teal-500',
      indigo: 'bg-indigo-500',
      amber: 'bg-amber-700',
      grey: 'bg-gray-500',
      silver: 'bg-gray-400',
      cyan: 'bg-cyan-500',
      lime: 'bg-lime-500',
      white: 'bg-white',
      ni: 'bg-gray-200'
    };
    return colorMap?.[color] || 'bg-gray-300';
  };

  // Helper to get text color based on cap color
  const getTextColorClass = (color) => {
    // Light colors need dark text for visibility
    const lightColors = ['white', 'yellow', 'lime', 'cyan', 'silver', 'ni'];
    return lightColors.includes(color) ? 'text-gray-900' : 'text-white';
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target || {};
    
    // Special handling for passage number - only allow integers
    if (name === 'passageNumber') {
      // Remove any non-digit characters
      const numericValue = value?.replace(/[^0-9]/g, '');
      setSampleFormData(prev => ({ ...prev, [name]: numericValue }));
    } else {
      setSampleFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // NEW: Handle grid size change with Appwrite
  const handleGridSizeChange = async (newGridType) => {
    if (!currentBox) return;

    const oldGridSize = currentBox?.type === '9x9' ? 9 : 10;
    const newGridSize = newGridType === '9x9' ? 9 : 10;

    if (newGridSize < oldGridSize) {
      const samplesOutOfRange = samples?.filter(sample =>
        sample?.row_index >= newGridSize || sample?.col_index >= newGridSize
      );

      if (samplesOutOfRange?.length > 0) {
        const confirmMessage = `Warning: Changing to ${newGridType} grid will remove ${samplesOutOfRange?.length} sample(s) that are outside the new grid boundaries.\n\nAffected positions: ${samplesOutOfRange?.map(s => s?.position)?.join(', ')}\n\nDo you want to continue?`;

        if (!window?.confirm(confirmMessage)) {
          return;
        }

        // Delete samples that are out of range
        for (const sample of samplesOutOfRange) {
          try {
            await databases.deleteDocument(
              DATABASE_ID,
              COLLECTIONS.SAMPLES,
              sample?.id
            );
          } catch (deleteError) {
            console.error('Error deleting sample:', deleteError);
          }
        }
      }
    }

    try {
      setError('');
      const newSampleCapacity = newGridSize * newGridSize;

      // Update box type and capacity
      await databases.updateDocument(
        DATABASE_ID,
        COLLECTIONS.BOXES,
        currentBoxId,
        {
          box_type: newGridType,
          max_capacity: newSampleCapacity
        }
      );

      // Reload box data
      await loadBoxData();

      setShowSampleForm(false);
      setSelectedPosition(null);
    } catch (err) {
      console.error('Error changing grid size:', err);
      setError(err?.message || 'Failed to change grid size');
    }
  };

  // NEW: Import handlers
  const handleImportClick = () => {
    setShowImportModal(true);
    setImportResults(null);
    setImportFile(null);
  };

  const handleFileUpload = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    setImportFile(file);
    setImportResults(null);
  };

  const handleImportConfirm = async () => {
    if (!importFile || !currentBoxId) {
      setError('Please upload a file');
      return;
    }

    try {
      setError('');
      setLoading(true);

      // Parse Excel file
      const rawData = await parseExcelFile(importFile);

      // Validate imported samples
      const { valid, errors, validSamples } = validateImportedSamples(rawData);

      if (!valid) {
        setImportResults({
          success: 0,
          failed: rawData?.length,
          errors: errors
        });
        setLoading(false);
        return;
      }

      // Import samples using bulk import
      const results = await sampleService?.bulkImport(currentBoxId, validSamples);
      setImportResults(results);

      // Reload box data if any were successful
      if (results?.success > 0) {
        await loadBoxData();
      }
    } catch (err) {
      console.error('Error importing samples:', err);
      setError(err?.message || 'Failed to import samples');
      setImportResults({
        success: 0,
        failed: 0,
        errors: [err?.message]
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImportCancel = () => {
    setShowImportModal(false);
    setImportResults(null);
    setImportFile(null);
  };

  const handleDownloadTemplate = () => {
    downloadImportTemplate('sample_import_template');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading box data...</p>
        </div>
      </div>
    );
  }

  if (!currentBox) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">No box selected</p>
          <Button onClick={() => navigate('/dewars')} className="bg-indigo-600 hover:bg-indigo-700 text-white">
            Go to Dewars Management
          </Button>
        </div>
      </div>
    );
  }

  const gridSize = currentBox?.type === '9x9' ? 9 : 10;
  const cellSize = 48 * zoomLevel;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {error && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 bg-red-50 border border-red-200 rounded-lg p-4 shadow-lg max-w-md">
          <p className="text-red-800 text-sm">{error}</p>
          <button
            onClick={() => setError('')}
            className="mt-2 text-xs text-red-600 hover:text-red-800 underline"
          >
            Dismiss
          </button>
        </div>
      )}
      {/* NEW: Drag selection indicator */}
      {isDragging && draggedPositions?.size > 1 && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 bg-purple-600 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">
              {draggedPositions?.size} cell{draggedPositions?.size > 1 ? 's' : ''} selected
            </span>
          </div>
          <div className="flex items-center gap-2 border-l border-purple-400 pl-4">
            <span className="text-xs">Double-click to add samples to selection</span>
          </div>
        </div>
      )}
      {/* MODIFIED: Copy mode indicator - simplified without empty cell counter */}
      {isCopyMode && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-[60] bg-purple-600 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Copy className="w-5 h-5" />
            <span className="text-sm font-medium">
              Copy Mode Active - Select cells to paste
            </span>
          </div>
          <div className="flex items-center gap-2 border-l border-purple-400 pl-4">
            <button
              onClick={handleCompleteCopy}
              disabled={selectedCells?.size === 0}
              className={`px-4 py-2 rounded-md font-semibold transition-colors flex items-center gap-2 ${
                selectedCells?.size === 0
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed' :'bg-white text-purple-600 hover:bg-purple-50'
              }`}
            >
              <Clipboard className="w-4 h-4" />
              Complete Copy{selectedCells?.size > 0 ? ` (${selectedCells?.size})` : ''}
            </button>
          </div>
          <button
            onClick={() => {
              setSelectedCells(new Set());
              setIsCopyMode(false);
              setCopiedSample(null);
            }}
            className="ml-2 hover:bg-purple-700 rounded-full p-1"
            title="Exit copy mode"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      {/* MODIFIED: Delete mode indicator - SECOND PRIORITY, positioned below copy mode */}
      {isDeleteMode && selectedSamples?.size > 0 && (
        <div className={`fixed ${isCopyMode && selectedCells?.size > 0 ? 'top-32' : 'top-20'} left-1/2 transform -translate-x-1/2 z-50 bg-red-600 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-4`}>
          <div className="flex items-center gap-2">
            <Trash2 className="w-5 h-5" />
            <span className="text-sm font-medium">
              {selectedSamples?.size} sample{selectedSamples?.size > 1 ? 's' : ''} flagged for deletion
            </span>
          </div>
          <div className="flex items-center gap-2 border-l border-red-400 pl-4">
            <button
              onClick={handleBatchDeleteSamples}
              className="px-4 py-2 bg-white text-red-600 hover:bg-red-50 rounded-md font-semibold transition-colors flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Delete Selected Samples
            </button>
          </div>
          <button
            onClick={() => {
              setSelectedSamples(new Set());
              setIsDeleteMode(false);
            }}
            className="ml-2 hover:bg-red-700 rounded-full p-1"
            title="Clear selection"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      {/* Header with Navigation Breadcrumb */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={() => navigate('/dewars')}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ChevronLeft className="w-5 h-5 mr-1" />
              <span className="text-sm font-medium">Back to Dewars</span>
            </button>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setZoomLevel(Math.max(0.5, zoomLevel - 0.25))}
                disabled={zoomLevel <= 0.5}
                className="p-2 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Zoom Out"
              >
                <ZoomOut className="w-4 h-4" />
              </button>
              <span className="text-sm font-medium text-gray-700 min-w-[60px] text-center">
                {Math.round(zoomLevel * 100)}%
              </span>
              <button
                onClick={() => setZoomLevel(Math.min(2, zoomLevel + 0.25))}
                disabled={zoomLevel >= 2}
                className="p-2 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Zoom In"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between text-sm text-gray-600 mb-3">
            <div className="flex items-center">
              <span className="font-semibold text-gray-900">{breadcrumb?.dewarName}</span>
              <span className="mx-2">→</span>
              <span className="font-semibold text-gray-900">Rack {breadcrumb?.rackNumber}</span>
              <span className="mx-2">→</span>
              <span className="font-semibold text-indigo-600">Box {breadcrumb?.boxName}</span>
            </div>

            {/* Liquid Nitrogen Level Gauge */}
            {currentDewar && (
              <div className="flex items-center gap-3 bg-white px-4 py-2 rounded-lg border border-gray-200 shadow-sm">
                <span className="text-xs font-medium text-gray-600">LN₂ Level:</span>
                <div className="flex items-center gap-2">
                  {/* Horizontal gauge */}
                  <div className="relative w-32 h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-300 ${
                        currentDewar?.liquidNitrogenLevel <= 4
                          ? 'bg-red-500'
                          : currentDewar?.liquidNitrogenLevel <= 8
                          ? 'bg-yellow-500'
                          : 'bg-green-500'
                      }`}
                      style={{
                        width: `${Math.min(100, (currentDewar?.liquidNitrogenLevel / 22) * 100)}%`
                      }}
                    />
                  </div>
                  {/* Level text */}
                  <span className={`text-sm font-bold ${
                    currentDewar?.liquidNitrogenLevel <= 4
                      ? 'text-red-600'
                      : currentDewar?.liquidNitrogenLevel <= 8
                      ? 'text-yellow-600'
                      : 'text-green-600'
                  }`}>
                    {currentDewar?.liquidNitrogenLevel}"
                  </span>
                  <span className="text-xs text-gray-500">/ 22"</span>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 overflow-x-auto">
            {tabs?.map((tab, index) => (
              <button
                key={tab?.id}
                onClick={() => switchTab(index)}
                className={`flex items-center gap-2 px-4 py-2 rounded-t-lg border-b-2 transition-colors whitespace-nowrap ${
                  activeTabIndex === index
                    ? 'bg-indigo-50 border-indigo-600 text-indigo-600 font-semibold' :'bg-gray-100 border-transparent text-gray-600 hover:bg-gray-200'
                }`}
              >
                <span>Box {tab?.boxName}</span>
                {tabs?.length > 1 && (
                  <button
                    onClick={(e) => closeTab(index, e)}
                    className="ml-1 hover:bg-gray-300 rounded-full p-0.5"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </button>
            ))}
            {tabs?.length < 5 && (
              <button
                onClick={addNewTab}
                className="flex items-center gap-1 px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
                title="Add another box tab"
              >
                <Plus className="w-4 h-4" />
                <span className="text-sm">Add Box</span>
              </button>
            )}
          </div>
        </div>
      </div>
      {/* MODIFIED: Copy Notification - top-right position, only shows temporarily */}
      {showCopyNotification && (
        <div className="fixed top-20 right-4 z-[55] bg-green-600 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 animate-fade-in">
          <Clipboard className="w-5 h-5" />
          <span className="font-medium">Sample copied! Click empty cells to paste.</span>
        </div>
      )}
      {/* Main Content - Three Column Layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Box Information */}
        <div className="w-1/4 bg-white border-r border-gray-200 overflow-y-auto p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Box Information</h2>
          
          <div className="space-y-4">
            <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Box Name</p>
              <p className="text-lg font-bold text-gray-900">{currentBox?.name}</p>
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-2">Grid Type</p>
              <div className="flex items-center gap-3">
                <select
                  value={currentBox?.type}
                  onChange={(e) => handleGridSizeChange(e?.target?.value)}
                  className="flex-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-indigo-600"
                >
                  <option value="9x9">9x9 Grid (81 samples)</option>
                  <option value="10x10">10x10 Grid (100 samples)</option>
                </select>
              </div>
              <p className="text-xs text-gray-600 mt-2">
                Current: <span className="font-semibold">{currentBox?.type}</span>
              </p>
            </div>

            <div className="bg-gradient-to-r from-green-50 to-teal-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Capacity</p>
              <p className="text-lg font-bold text-gray-900">{currentBox?.occupied}/{currentBox?.samples}</p>
              <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                <div 
                  className="h-2 rounded-full bg-green-600"
                  style={{ width: `${(currentBox?.occupied / currentBox?.samples) * 100}%` }}
                />
              </div>
              <p className="text-xs text-gray-600 mt-1">
                {Math.round((currentBox?.occupied / currentBox?.samples) * 100)}% occupied
              </p>
            </div>

            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-2">Recent Modifications</p>
              <p className="text-xs text-gray-500">No recent changes</p>
            </div>

            {/* NEW: Import from Excel */}
            <div className="pt-4 border-t border-gray-200">
              <button
                onClick={handleImportClick}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-semibold transition-colors"
              >
                <Upload className="w-5 h-5" />
                Import from Excel
              </button>
              <p className="text-xs text-gray-500 mt-2 text-center">
                Bulk import samples into this box
              </p>
            </div>

            {/* Navigation Between Boxes */}
            <div className="pt-4 border-t border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Navigate Boxes</h3>
              <div className="flex gap-2">
                <button
                  onClick={() => navigateToAdjacentBox('prev')}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded text-sm"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Prev
                </button>
                <button
                  onClick={() => navigateToAdjacentBox('next')}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded text-sm"
                >
                  Next
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* NEW: Multi-select instructions */}
            <div className="pt-4 border-t border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Delete Samples</h3>
              <div className="bg-red-50 p-3 rounded-lg">
                <ul className="text-xs text-gray-600 space-y-2">
                  <li className="flex items-start gap-2">
                    <Trash2 className="w-3 h-3 mt-0.5 flex-shrink-0 text-red-600" />
                    <span>Double-click on filled cells to flag them for deletion (toggle on/off)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 font-bold">•</span>
                    <span>Multiple cells can be double-clicked and flagged</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 font-bold">•</span>
                    <span>Click "Delete Selected Samples" button in the popup to confirm deletion</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <kbd className="px-1 py-0.5 bg-gray-200 rounded text-xs font-mono">Esc</kbd>
                    <span>Press Escape to clear all deletion flags</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* MODIFIED: Copy/Paste Instructions - simplified */}
            <div className="pt-4 border-t border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Edit & Copy Samples</h3>
              <div className="bg-blue-50 p-3 rounded-lg">
                <ul className="text-xs text-gray-600 space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="font-bold text-blue-600">•</span>
                    <span><strong>Single-click</strong> on a filled cell to edit sample information</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-bold text-blue-600">1.</span>
                    <span><strong>Right-click (Control-click on Mac)</strong> an occupied cell to copy it</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-bold text-blue-600">2.</span>
                    <span><strong>Click</strong> empty cells to select where to paste (multiple allowed)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-bold text-blue-600">3.</span>
                    <span><strong>Complete Copy:</strong> Click "Complete Copy" button in the top notification</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <kbd className="px-1 py-0.5 bg-gray-200 rounded text-xs font-mono">Esc</kbd>
                    <span>Exit copy mode</span>
                  </li>
                </ul>
                <div className="mt-3 pt-3 border-t border-blue-200">
                  <p className="text-xs font-semibold text-blue-800 mb-1">Visual Indicators:</p>
                  <ul className="text-xs text-gray-600 space-y-1">
                    <li>🟢 Green cells = Available for selection</li>
                    <li>🟣 Purple checkmark = Selected for paste</li>
                  </ul>
                </div>
              </div>
              {copiedSample && (
                <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded text-xs">
                  <p className="font-semibold text-green-800 mb-1">Copied Sample:</p>
                  <p className="text-green-700">{copiedSample?.sampleName}</p>
                  <button
                    onClick={() => {
                      handleClearCopiedSample();
                      setIsCopyMode(false);
                      setSelectedCells(new Set());
                    }}
                    className="mt-2 text-xs text-red-600 hover:text-red-800 underline"
                  >
                    Clear and exit copy mode
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Center Panel - Grid Visualization */}
        <div className="flex-1 bg-gray-100 overflow-auto p-6 flex items-center justify-center">
          <div className="inline-block">
            <div className="flex gap-2 mb-2">
              <div style={{ width: `${cellSize}px`, height: `${cellSize}px` }} />
              {Array?.from({ length: gridSize }, (_, i) => (
                <div
                  key={i}
                  style={{ width: `${cellSize}px`, height: `${cellSize}px` }}
                  className="flex items-center justify-center text-sm font-semibold text-gray-700"
                >
                  {i + 1}
                </div>
              ))}
            </div>

            {Array?.from({ length: gridSize }, (_, rowIndex) => (
              <div key={rowIndex} className="flex gap-2 mb-2">
                <div
                  style={{ width: `${cellSize}px`, height: `${cellSize}px` }}
                  className="flex items-center justify-center text-sm font-semibold text-gray-700"
                >
                  {String.fromCharCode(65 + rowIndex)}
                </div>

                {Array?.from({ length: gridSize }, (_, colIndex) => {
                  const cell = sampleGrid?.find(
                    c => c?.row === rowIndex && c?.col === colIndex
                  );

                  if (!cell) return null;

                  const isSelected = cell?.dbId && selectedSamples?.has(cell?.dbId);
                  const isCopySelected = selectedCells?.has(cell?.position);
                  const isEmptyCell = !cell?.occupied && !cell?.dbId;

                  return (
                    <button
                      key={colIndex}
                      onClick={(e) => handleGridCellClick(cell?.position, e)}
                      onDoubleClick={(e) => handleCellDoubleClick(cell?.position, e)}
                      onContextMenu={(e) => handleCellRightClick(cell?.position, e)}
                      onMouseEnter={() => setHoveredPosition(cell?.position)}
                      onMouseLeave={() => setHoveredPosition(null)}
                      style={{ width: `${cellSize}px`, height: `${cellSize}px` }}
                      className={`border-2 rounded-lg transition-all flex items-center justify-center text-xs font-bold relative ${
                        // Copy mode selected empty cell - purple highlight with checkmark
                        isCopySelected && isEmptyCell
                          ? 'ring-4 ring-purple-500 scale-105 border-purple-600 bg-purple-100'
                          // Copy mode with occupied cell selected (should not happen, but handle gracefully)
                          : isCopySelected && !isEmptyCell
                          ? 'ring-2 ring-yellow-400 border-yellow-500 bg-yellow-50'
                          // Deletion mode - red ring
                          : isSelected
                          ? 'ring-4 ring-red-500 scale-105 border-red-600 shadow-lg'
                          // Occupied cell normal state
                          : cell?.occupied
                          ? `${getColorClass(cell?.capColor)} border-gray-400 hover:border-gray-900 ${getTextColorClass(cell?.capColor)} hover:ring-2 hover:ring-gray-500`
                          // Copy mode active - highlight empty cells as green (available for selection)
                          : isCopyMode
                          ? 'bg-green-50 border-green-400 hover:border-green-600 hover:bg-green-100 cursor-pointer shadow-sm'
                          // Normal empty cell
                          : 'bg-white border-gray-300 hover:border-indigo-500 hover:bg-indigo-50'
                      } ${
                        selectedPosition === cell?.position && !isSelected && !isCopySelected
                          ? 'ring-4 ring-indigo-500 scale-105' : ''
                      }`}
                      title={
                        isCopySelected && isEmptyCell
                          ? `${cell?.position} - Selected for copy\nClick "Complete Copy" button to proceed`
                          : isCopySelected && !isEmptyCell
                          ? `${cell?.position} - OCCUPIED (should not be selected)\nThis cell cannot be used for copying`
                          : cell?.occupied
                          ? `${cell?.position} - ${cell?.sampleName || 'Sample'}${cell?.passageNumber ? ` (P${cell?.passageNumber})` : ''}\nSingle-click to edit\nDouble-click to flag for deletion\nRight-click (Control-click on Mac) to copy this sample`
                          : isCopyMode
                          ? `${cell?.position} - Empty cell (available)\nClick to select for copy\nUse "Complete Copy" button to finish`
                          : `${cell?.position} - Empty cell\nClick to add sample\nRight-click (Control-click on Mac) occupied cell to start copy mode`
                      }
                    >
                      {/* Visual indicator for copy-selected empty cells */}
                      {isCopySelected && isEmptyCell && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-full h-full flex items-center justify-center">
                            <svg className="w-8 h-8 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        </div>
                      )}
                      
                      {/* Warning indicator for occupied cells incorrectly selected */}
                      {isCopySelected && !isEmptyCell && (
                        <div className="absolute inset-0 flex items-center justify-center bg-yellow-400 bg-opacity-30">
                          <X className="w-6 h-6 text-yellow-700" />
                        </div>
                      )}
                      
                      {/* Deletion flag indicator */}
                      {isSelected && (
                        <div className="absolute top-1 right-1">
                          <div className="w-3 h-3 bg-red-600 rounded-full border border-white flex items-center justify-center">
                            <X className="w-2 h-2 text-white" />
                          </div>
                        </div>
                      )}
                      
                      {/* Occupied cell content display */}
                      {cell?.occupied && !isCopySelected && (
                        <>
                          {cell?.capColor === 'ni' ? (
                            <span className="text-gray-700">ni</span>
                          ) : cell?.passageNumber ? (
                            <span className={`${getTextColorClass(cell?.capColor)} font-semibold drop-shadow-md`}>
                              P{cell?.passageNumber}
                            </span>
                          ) : null}
                        </>
                      )}
                      
                      {/* Hover tooltip */}
                      {hoveredPosition === cell?.position && cell?.occupied && !isCopyMode && (
                        <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                          {cell?.sampleName || 'Sample'}
                          {cell?.passageNumber && <span> (P{cell?.passageNumber})</span>}
                          <br />
                          <span className="text-xs opacity-75">Right-click (Ctrl-click on Mac) to copy</span>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        {/* Right Panel - Sample Entry Form */}
        <div className="w-1/4 bg-white border-l border-gray-200 overflow-y-auto p-6">
          {showSampleForm ? (
            <>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-900">
                  {window.batchAddPositions && window.batchAddPositions?.length > 1
                    ? `Add Sample to ${window.batchAddPositions?.length} Positions`
                    : sampleGrid?.find(c => c?.position === selectedPosition)?.occupied
                    ? 'Edit Sample' :'Add Sample'
                  }
                </h2>
                <button
                  onClick={() => {
                    setShowSampleForm(false);
                    setSelectedPosition(null);
                    window.batchAddPositions = null;
                    setDraggedPositions(new Set());
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="mb-4 p-3 bg-indigo-50 rounded-lg">
                {window.batchAddPositions && window.batchAddPositions?.length > 1 ? (
                  <div>
                    <p className="text-sm text-gray-700 font-semibold mb-2">
                      Batch Add to Positions:
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {window.batchAddPositions?.slice(0, 10)?.map(pos => (
                        <span key={pos} className="text-xs bg-purple-200 text-purple-800 px-2 py-1 rounded">
                          {pos}
                        </span>
                      ))}
                      {window.batchAddPositions?.length > 10 && (
                        <span className="text-xs text-gray-600">
                          +{window.batchAddPositions?.length - 10} more
                        </span>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">Position:</span> {selectedPosition}
                  </p>
                )}
              </div>

              <div className="mb-4">
                <label className="text-sm font-medium text-gray-700 block mb-2">Cap Color</label>
                <div className="flex flex-wrap gap-2">
                  {['red', 'blue', 'green', 'yellow', 'purple', 'orange', 'pink', 'teal', 'indigo', 'amber', 'cyan', 'lime', 'grey', 'silver', 'white']?.map(color => (
                    <button
                      key={color}
                      onClick={() => setSelectedCapColor(color)}
                      className={`w-8 h-8 rounded-full border-4 transition-all ${
                        selectedCapColor === color ? 'border-gray-900 scale-110' : 'border-gray-300'
                      } ${getColorClass(color)}`}
                      title={color === 'amber' ? 'brown' : color}
                    />
                  ))}
                  <button
                    onClick={() => setSelectedCapColor('ni')}
                    className={`w-8 h-8 rounded-full border-4 transition-all flex items-center justify-center text-xs font-bold ${
                      selectedCapColor === 'ni' ? 'border-gray-900 scale-110' : 'border-gray-300'
                    } bg-gray-200`}
                    title="No Cap Insert"
                  >
                    ni
                  </button>
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  Selected: <span className="font-semibold capitalize">{selectedCapColor === 'ni' ? 'No Cap Insert' : selectedCapColor === 'amber' ? 'brown' : selectedCapColor}</span>
                </p>
              </div>

              {copiedSample && sampleFormData?.sampleName === copiedSample?.sampleName && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Clipboard className="w-4 h-4 text-green-600" />
                    <p className="text-sm text-green-800 font-medium">
                      Pasted sample data - Position will be: {selectedPosition}
                    </p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSampleSubmit} className="space-y-4">
                <Input
                  label="Sample Name"
                  name="sampleName"
                  value={sampleFormData?.sampleName}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter sample name"
                />

                <Input
                  label="Freeze Date"
                  name="freezeDate"
                  type="date"
                  value={sampleFormData?.freezeDate}
                  onChange={handleInputChange}
                  required
                />

                <Input
                  label="Frozen By"
                  name="frozenBy"
                  value={sampleFormData?.frozenBy}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., Dr. Smith"
                />

                <Input
                  label="PI (Principal Investigator)"
                  name="pi"
                  value={sampleFormData?.pi}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., Dr. Anderson"
                />

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Passage Number
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">
                      P
                    </span>
                    <input
                      type="text"
                      name="passageNumber"
                      value={sampleFormData?.passageNumber}
                      onChange={handleInputChange}
                      placeholder="43"
                      pattern="[0-9]*"
                      inputMode="numeric"
                      className="w-full rounded-md border border-gray-300 bg-white pl-8 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-600"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Enter only the number (e.g., 43). Display will show "P43"
                  </p>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Notes</label>
                  <textarea
                    name="notes"
                    value={sampleFormData?.notes}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-600"
                    placeholder="Enter any additional notes..."
                  />
                </div>

                <div className="flex flex-col gap-2 pt-4">
                  {sampleGrid?.find(c => c?.position === selectedPosition)?.occupied && (
                    <Button
                      type="button"
                      onClick={handleRemoveSample}
                      className="w-full bg-red-600 text-white hover:bg-red-700"
                    >
                      Remove Sample
                    </Button>
                  )}
                  <Button
                    type="submit"
                    className="w-full bg-indigo-600 text-white hover:bg-indigo-700"
                  >
                    {sampleGrid?.find(c => c?.position === selectedPosition)?.occupied
                      ? 'Update Sample' :'Add Sample'
                    }
                  </Button>
                </div>
              </form>
            </>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500 mb-4">Click on a grid position to add or edit a sample</p>
              <div className="bg-blue-50 p-4 rounded-lg text-left">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Instructions:</h3>
                <ol className="list-decimal list-inside text-xs text-gray-600 space-y-1">
                  <li>Click on an empty (white) grid cell to add a new sample</li>
                  <li><strong>Edit Sample:</strong> Single-click on a filled cell to edit its information</li>
                  <li><strong>Delete Samples:</strong> Double-click on filled cells to flag for deletion (toggle on/off)</li>
                  <li>Multiple cells can be flagged by double-clicking each one</li>
                  <li>Use "Delete Selected Samples" button in left panel to confirm deletion</li>
                  <li><strong>Copy Mode:</strong> Right-click (Control-click on Mac) on a cell to start copy mode</li>
                  <li><strong>Select Cells:</strong> Click on empty cells to select them for copying</li>
                  <li><strong>Complete Copy:</strong> Click "Complete Copy" button in the top notification</li>
                  <li>Press Escape to clear deletion flags or exit copy mode</li>
                  <li>Passage number: Enter only digits (e.g., 43), display shows "P43"</li>
                  <li>Use zoom controls to adjust grid visibility</li>
                  <li>Switch between tabs to manage multiple boxes</li>
                </ol>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* NEW: Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">Import Samples from Excel</h3>
              <button
                onClick={handleImportCancel}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Instructions:</strong> Download the template, fill in your sample data, and upload the completed file.
                  Samples will be imported into <strong>{currentBox?.name}</strong>. The Sample ID column will be auto-generated during import.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleDownloadTemplate}
                  className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Template
                </button>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-900">Excel File</label>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileUpload}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                />
                {importFile && (
                  <p className="text-sm text-gray-600">
                    Selected: {importFile?.name}
                  </p>
                )}
              </div>

              {importResults && (
                <div className={`p-4 rounded-lg border ${
                  importResults?.success > 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                }`}>
                  <h4 className="font-semibold mb-2">Import Results</h4>
                  <div className="space-y-1 text-sm">
                    <p className="text-green-800">Successful: {importResults?.success}</p>
                    <p className="text-red-800">Failed: {importResults?.failed}</p>
                    {importResults?.errors && importResults?.errors?.length > 0 && (
                      <div className="mt-3">
                        <p className="font-semibold text-red-900 mb-1">Errors:</p>
                        <ul className="list-disc list-inside space-y-1 text-red-800 max-h-40 overflow-y-auto">
                          {importResults?.errors?.map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex gap-3 justify-end border-t border-gray-200">
              <button
                onClick={handleImportCancel}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
              >
                {importResults ? 'Close' : 'Cancel'}
              </button>
              {!importResults && (
                <button
                  onClick={handleImportConfirm}
                  disabled={!importFile}
                  className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Import Samples
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BoxViewSampleManagement;